/**
 * Which Google sign-in flow a device can actually complete.
 *
 * `signInWithPopup` does not work on mobile web. iOS Safari blocks the popup
 * silently, and in-app browsers (Instagram, X, LinkedIn, the Gmail viewer)
 * refuse to open one at all. The user taps "Sign in with Google" and nothing
 * happens — no error, no popup, no way forward.
 *
 * The Firebase-recommended fix is `signInWithRedirect`: the page itself
 * navigates to accounts.google.com and returns with the credential available
 * via `getRedirectResult`.
 *
 * This lives in the shared package because BOTH front ends need it and only
 * one had it. apps/ui_v2 implemented the detection inline; apps/console called
 * `signInWithPopup` unconditionally, so every mobile visitor to the console
 * hit the dead state above.
 *
 * Detection prefers `navigator.userAgentData.mobile`, which is a direct
 * answer, and falls back to a UA regex for Safari and older Android where that
 * API does not exist.
 *
 * The error asymmetry drives the default: a false POSITIVE costs a desktop
 * user one full-page reload instead of a popup, and sign-in still succeeds. A
 * false NEGATIVE strands a mobile user with no way to sign in. When unsure,
 * prefer redirect.
 */
export function isMobileWeb(): boolean {
  if (typeof navigator === 'undefined') return false

  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData
  if (uaData && typeof uaData.mobile === 'boolean') return uaData.mobile

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}
